import { Component } from 'react'

/**
 * Error boundary global — empêche qu'un crash dans une page ne mange toute l'app.
 *
 * Deux comportements :
 *  1. Auto-réparation des crashs de cache : après un déploiement, un navigateur
 *     qui a gardé un vieux bundle/chunk peut planter ("x is not a function",
 *     "Failed to fetch dynamically imported module"…). On recharge alors la page
 *     UNE fois par session (garde anti-boucle) pour récupérer le build à jour.
 *  2. Sinon : message friendly + détail copiable (message + stack) pour debug.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null, copied: false }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, info) {
    console.error('[ErrorBoundary]', error, info)
    // Auto-reload une seule fois par session : règle les crashs dus à un
    // index.html / chunk périmé après déploiement. Si ça replante après le
    // reload, le flag est déjà posé -> on affiche le détail au lieu de boucler.
    try {
      const already = sessionStorage.getItem('eb_auto_reloaded')
      if (!already) {
        sessionStorage.setItem('eb_auto_reloaded', String(Date.now()))
        window.location.reload()
      }
    } catch {
      /* sessionStorage indisponible : on affiche simplement le détail */
    }
  }

  copyDetail = () => {
    const e = this.state.error
    const detail = `${e?.message || 'Erreur'}\n\n${e?.stack || ''}`.trim()
    try {
      navigator.clipboard.writeText(detail)
      this.setState({ copied: true })
      setTimeout(() => this.setState({ copied: false }), 2000)
    } catch {
      /* clipboard indisponible */
    }
  }

  render() {
    if (this.state.hasError) {
      const msg = this.state.error?.message || 'Erreur inattendue'
      const stack = this.state.error?.stack || ''
      return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-dark-950">
          <div className="card max-w-lg w-full text-center">
            <div className="text-5xl mb-3">⚠️</div>
            <h2 className="text-dark-50 font-semibold mb-2">Cette page a planté</h2>
            <p className="text-dark-400 text-sm mb-3">L'app a évité le crash global — tu peux continuer à utiliser le reste.</p>
            <p className="text-dark-300 text-xs mb-2 font-mono break-words bg-dark-900/60 rounded p-2">{msg}</p>
            {stack && (
              <pre className="text-dark-600 text-[10px] text-left max-h-32 overflow-auto mb-4 bg-dark-900/60 rounded p-2 whitespace-pre-wrap break-words">
                {stack.split('\n').slice(0, 6).join('\n')}
              </pre>
            )}
            <div className="flex gap-2 justify-center flex-wrap">
              <button
                onClick={() => this.setState({ hasError: false, error: null })}
                className="btn-primary text-sm"
              >
                Réessayer
              </button>
              <button onClick={this.copyDetail} className="btn-secondary text-sm">
                {this.state.copied ? '✓ Copié' : '📋 Copier le détail'}
              </button>
              <a href="/dashboard" className="btn-secondary text-sm">
                ← Tableau de bord
              </a>
              <button
                onClick={() => window.location.reload()}
                className="btn-secondary text-sm"
              >
                Recharger
              </button>
            </div>
            <p className="text-dark-600 text-[11px] mt-4">
              Si le problème persiste, clique « Copier le détail » et colle-le moi.
            </p>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
